import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  UploadCloud, Sparkles, ArrowRight, CheckCircle2, FileSpreadsheet,
  Undo2, ChevronDown, ChevronUp, History, BookmarkCheck, Wand2, X,
} from "lucide-react";

const ENTITY_LABELS = {
  facilities: "Facilities",
  nps: "Nurse Practitioners",
  medical_directors: "Medical Directors",
  cnas: "CNAs",
  claims: "Claims",
  issues: "Issues",
  meetings: "Meetings",
};

const TARGET_FIELDS = {
  facilities: ["", "name", "address", "city", "state", "zip", "phone", "np_name", "medical_director_name", "cna_name", "dor_name", "dor_phone", "dor_email", "np_approval_status", "start_date", "patient_count", "visits_per_month", "visits_per_week", "cpt_codes", "last_np_visit", "next_meeting", "claims_status", "notes"],
  nps: ["", "name", "phone", "email", "address", "notes"],
  medical_directors: ["", "name", "phone", "email", "notes"],
  cnas: ["", "name", "phone", "email", "notes"],
  claims: ["", "facility_name", "np_name", "patient_name", "amount", "submitted_date", "status", "notes"],
  issues: ["", "title", "description", "severity", "status", "facility_name", "np_name", "due_date"],
  meetings: ["", "title", "when", "attendees", "facility_name", "np_name", "notes"],
};

export default function SmartImportPage() {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [analysis, setAnalysis] = useState(null); // { filename, token, sheets: [] }
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState({}); // sheet_name -> result
  const [batches, setBatches] = useState([]);

  const loadBatches = useCallback(async () => {
    try { const { data } = await api.get("/import/batches"); setBatches(data); }
    catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[import/batches] load failed:", e?.message || e);
    }
  }, []);
  useEffect(() => { loadBatches(); }, [loadBatches]);

  const analyze = async (f) => {
    setBusy(true); setAnalysis(null); setApplied({});
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/import/smart/v2/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAnalysis(data);
      toast.success(`${data.sheets.length} sheet(s) analyzed`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Analyze failed");
    } finally { setBusy(false); }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFile(f); analyze(f);
  };
  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0]; if (!f) return;
    setFile(f); analyze(f);
  };

  const updateSheet = (idx, patch) => {
    setAnalysis((prev) => ({
      ...prev,
      sheets: prev.sheets.map((s, i) => i === idx ? { ...s, ...patch } : s),
    }));
  };

  const setEntity = (idx, entity) => updateSheet(idx, { entity, target_fields: TARGET_FIELDS[entity] || [] });
  const setColMapping = (idx, col, tgt) => {
    const s = analysis.sheets[idx];
    updateSheet(idx, { mapping: { ...s.mapping, [col]: tgt === "__blank__" ? "" : tgt } });
  };
  const setInstruction = (idx, v) => updateSheet(idx, { instruction: v });
  const removeFilter = (idx, fi) => {
    const s = analysis.sheets[idx];
    updateSheet(idx, { filters: (s.filters || []).filter((_, i) => i !== fi) });
  };

  const reclassify = async (idx) => {
    const s = analysis.sheets[idx];
    if (!s.instruction?.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/import/smart/v2/reclassify", {
        entity: s.entity, mapping: s.mapping,
        token: analysis.token, filename: analysis.filename,
        sheet_name: s.sheet_name, instruction: s.instruction,
      });
      updateSheet(idx, {
        entity: data.entity, mapping: data.mapping,
        filters: data.filters || [], confidence: data.confidence,
        reason: data.reason, target_fields: data.target_fields,
        row_count: data.row_count, diffs: data.diffs,
      });
      toast.success("Reworked with your instructions");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reclassify failed");
    } finally { setBusy(false); }
  };

  const applySheet = async (idx) => {
    const s = analysis.sheets[idx];
    setBusy(true);
    try {
      const { data } = await api.post("/import/smart/v2/apply", {
        entity: s.entity, mapping: s.mapping,
        filters: s.filters || [],
        token: analysis.token, filename: analysis.filename,
        sheet_name: s.sheet_name,
      });
      setApplied((prev) => ({ ...prev, [s.sheet_name]: data }));
      toast.success(`${s.sheet_name}: ${data.created} new · ${data.updated} updated`);
      loadBatches();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
    } finally { setBusy(false); }
  };

  const undoBatch = async (id) => {
    if (!window.confirm("Undo this import? It will remove the rows created in that run.")) return;
    try {
      const { data } = await api.post(`/import/batches/${id}/undo`);
      toast.success(`Reverted ${data.removed} records`);
      loadBatches();
    } catch { toast.error("Undo failed"); }
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl">
      <div className="text-xs uppercase tracking-[0.15em] text-[#8A847A] flex items-center gap-2">
        <Sparkles className="h-3.5 w-3.5" /> AI-assisted
      </div>
      <h1 className="heading text-3xl sm:text-4xl font-bold text-[#292723] mt-2">Smart Import</h1>
      <p className="text-sm text-[#5C5850] mt-1.5 max-w-2xl">
        Drop any Excel or CSV file — multi-sheet workbooks welcome. GPT 5.6 Terra classifies each sheet, remembers your mapping for next time, and shows a per-row diff before anything is written.
      </p>

      <div
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        className="mt-8 border-2 border-dashed border-[#E5E0D8] rounded-md bg-white p-10 text-center cursor-pointer hover:bg-[#F0EBE1] transition-colors"
        onClick={() => fileRef.current?.click()}
        data-testid="smart-import-dropzone"
      >
        <UploadCloud className="h-8 w-8 mx-auto text-[#4A6B5D]" />
        <div className="mt-3 heading font-semibold">{file ? file.name : "Drop Excel or CSV here"}</div>
        <div className="text-xs text-[#8A847A] mt-1">.xlsx, .xls, .csv — multi-sheet workbooks supported</div>
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.txt,text/csv" className="hidden" onChange={onPick} data-testid="smart-import-input" />
      </div>

      {busy && <div className="mt-4 text-sm text-[#5C5850]">Working…</div>}

      {/* Sheet cards */}
      {analysis?.sheets?.map((s, idx) => (
        <SheetCard
          key={s.sheet_name}
          sheet={s}
          idx={idx}
          applied={applied[s.sheet_name]}
          onEntityChange={(v) => setEntity(idx, v)}
          onColMap={(c, v) => setColMapping(idx, c, v)}
          onInstructionChange={(v) => setInstruction(idx, v)}
          onReclassify={() => reclassify(idx)}
          onRemoveFilter={(fi) => removeFilter(idx, fi)}
          onApply={() => applySheet(idx)}
          busy={busy}
        />
      ))}

      {/* Recent imports / undo */}
      {batches.length > 0 && (
        <div className="mt-10 bg-white border border-[#E5E0D8] rounded-md" data-testid="batches-panel">
          <div className="px-5 py-4 border-b border-[#E5E0D8] flex items-center gap-2">
            <History className="h-4 w-4 text-[#4A6B5D]" />
            <h3 className="heading font-semibold">Recent imports</h3>
          </div>
          <div className="divide-y divide-[#E5E0D8]">
            {batches.map((b) => (
              <div key={b.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`batch-${b.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#292723]">
                    {ENTITY_LABELS[b.entity] || b.entity}
                    <span className="text-[#8A847A] font-normal"> · {b.filename}</span>
                    {b.sheet_name && b.sheet_name !== "Sheet1" && <span className="text-[#8A847A] font-normal"> / {b.sheet_name}</span>}
                  </div>
                  <div className="text-xs text-[#8A847A] mt-0.5">
                    {b.created_count} new · {b.updated_count} updated · {new Date(b.created_at).toLocaleString()}
                    {b.undone && <span className="text-[#C17D6E] ml-2">· reverted{b.removed_count ? ` (${b.removed_count})` : ""}</span>}
                  </div>
                </div>
                {!b.undone && (
                  <Button variant="outline" size="sm" onClick={() => undoBatch(b.id)} className="border-[#E5E0D8] text-[#C17D6E]" data-testid={`undo-${b.id}`}>
                    <Undo2 className="h-4 w-4 mr-1.5" /> Undo
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 bg-white border border-[#E5E0D8] rounded-md p-5">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="h-4 w-4 text-[#4A6B5D]" />
          <h3 className="heading font-semibold">Tip</h3>
        </div>
        <p className="text-sm text-[#5C5850] mt-2">
          You can also just message me the file in chat — same result. Re-importing a file with the same column layout uses your saved mapping automatically.
        </p>
      </div>
    </div>
  );
}

function SheetCard({ sheet, idx, applied, onEntityChange, onColMap, onInstructionChange, onReclassify, onRemoveFilter, onApply, busy }) {
  const [showDiffs, setShowDiffs] = useState(false);
  const diffs = sheet.diffs || { new: 0, updated: 0, rows: [] };
  const availableTargets = useMemo(
    () => (TARGET_FIELDS[sheet.entity] || []).filter(Boolean),
    [sheet.entity],
  );
  return (
    <div className="mt-8 bg-white border border-[#E5E0D8] rounded-md p-6" data-testid={`sheet-card-${idx}`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850] flex items-center gap-2">
            <FileSpreadsheet className="h-3.5 w-3.5" /> Sheet · {sheet.sheet_name}
            {sheet.from_saved && (
              <Badge variant="outline" className="border-[#b3d0bb] bg-[#DCE8DF] text-[#2f5238] rounded-full text-[10px] px-2 py-0 h-5">
                <BookmarkCheck className="h-3 w-3 mr-1" /> saved mapping
              </Badge>
            )}
          </div>
          <div className="mt-1 heading text-lg font-semibold">
            {sheet.row_count} rows · confidence {Math.round((sheet.confidence || 0) * 100)}%
          </div>
          {sheet.reason && <div className="text-sm text-[#5C5850] italic mt-1">{sheet.reason}</div>}
        </div>
        <div className="min-w-[220px]">
          <div className="text-[10px] uppercase tracking-[0.06em] text-[#5C5850] mb-1">Import as</div>
          <select
            value={sheet.entity || ""}
            onChange={(e) => onEntityChange(e.target.value)}
            className="h-10 rounded-md border border-[#E5E0D8] bg-white px-3 text-sm w-full"
            data-testid={`entity-picker-${idx}`}
          >
            {Object.entries(ENTITY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>

      {/* Diff summary */}
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="border-[#b3d0bb] bg-[#DCE8DF] text-[#2f5238] rounded-full">
          {diffs.new} new
        </Badge>
        <Badge variant="outline" className="border-[#e2ca97] bg-[#F5E3C4] text-[#8a5f1e] rounded-full">
          {diffs.updated} update{diffs.updated === 1 ? "" : "s"}
        </Badge>
        {diffs.rows?.length > 0 && (
          <button onClick={() => setShowDiffs(!showDiffs)} className="text-xs text-[#4A6B5D] font-medium inline-flex items-center gap-1" data-testid={`toggle-diffs-${idx}`}>
            {showDiffs ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {showDiffs ? "Hide" : "Show"} per-row diff
          </button>
        )}
      </div>

      {/* Custom AI instructions */}
      <div className="mt-6 bg-[#F7F5F0] border border-[#E5E0D8] rounded-md p-4">
        <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850] flex items-center gap-2 mb-2">
          <Wand2 className="h-3.5 w-3.5 text-[#4A6B5D]" /> Tell the AI what to do (optional)
        </div>
        <textarea
          value={sheet.instruction || ""}
          onChange={(e) => onInstructionChange(e.target.value)}
          placeholder='e.g. "Only rows from Maryland" · "Use column D as DOR phone, skip column C" · "Import only facilities with more than 20 patients"'
          className="w-full min-h-[72px] rounded-md border border-[#E5E0D8] bg-white p-2 text-sm resize-y"
          data-testid={`instruction-${idx}`}
        />
        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="text-[11px] text-[#8A847A]">GPT 5.6 Terra will re-map columns and add row filters based on this.</div>
          <Button
            variant="outline"
            size="sm"
            onClick={onReclassify}
            disabled={busy || !sheet.instruction?.trim()}
            className="border-[#4A6B5D] text-[#4A6B5D]"
            data-testid={`reclassify-${idx}`}
          >
            <Wand2 className="h-4 w-4 mr-1.5" /> Apply instructions
          </Button>
        </div>

        {(sheet.filters?.length || 0) > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="text-[11px] uppercase tracking-[0.06em] text-[#5C5850]">Filters:</span>
            {sheet.filters.map((f, fi) => (
              <Badge
                key={`${f.column}-${f.op}-${fi}`}
                variant="outline"
                className="border-[#e2ca97] bg-[#F5E3C4] text-[#8a5f1e] rounded-full gap-1"
                data-testid={`filter-${idx}-${fi}`}
              >
                <span className="font-mono">{f.column}</span>
                <span className="opacity-70">{f.op}</span>
                {f.value && <span className="font-mono">{f.value}</span>}
                <button onClick={() => onRemoveFilter(fi)} className="hover:text-[#C17D6E]">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      {showDiffs && diffs.rows?.length > 0 && (
        <div className="mt-3 max-h-[280px] overflow-y-auto bg-[#F7F5F0] rounded-md p-3 space-y-2 text-xs" data-testid={`diffs-${idx}`}>
          {diffs.rows.map((r) => (
            <div key={`${r.key}-${r.is_new ? "new" : "upd"}`} className="bg-white rounded p-2 border border-[#E5E0D8]">
              <div className="flex items-center gap-2">
                <span className={`inline-block h-2 w-2 rounded-full ${r.is_new ? "bg-[#507A5E]" : "bg-[#D9A05B]"}`} />
                <span className="font-medium">{r.key}</span>
                <span className="text-[#8A847A]">{r.is_new ? "· new" : "· update"}</span>
              </div>
              {!r.is_new && Object.entries(r.changes).length > 0 && (
                <div className="mt-1.5 space-y-0.5">
                  {Object.entries(r.changes).map(([f, ch]) => (
                    <div key={f} className="text-[#5C5850] flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[#4A6B5D]">{f}</span>
                      <span className="text-[#C17D6E] line-through truncate max-w-[200px]">{ch.before || "—"}</span>
                      <ArrowRight className="h-3 w-3 text-[#8A847A]" />
                      <span className="text-[#2f5238] truncate max-w-[200px]">{ch.after}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Mapping table */}
      <div className="mt-6">
        <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850] mb-2">Column mapping</div>
        <div className="bg-[#F7F5F0] rounded-md p-3 divide-y divide-[#E5E0D8] max-h-[320px] overflow-y-auto">
          {sheet.columns.map((col) => (
            <div key={col} className="flex items-center gap-3 py-2">
              <div className="flex-1 min-w-0 text-sm font-mono truncate" title={col}>{col}</div>
              <ArrowRight className="h-4 w-4 text-[#8A847A] shrink-0" />
              <select
                value={sheet.mapping[col] || "__blank__"}
                onChange={(e) => onColMap(col, e.target.value)}
                className="h-9 rounded-md border border-[#E5E0D8] bg-white px-2 text-sm min-w-[180px]"
                data-testid={`map-${idx}-${col}`}
              >
                <option value="__blank__">— skip —</option>
                {(TARGET_FIELDS[sheet.entity] || []).filter(Boolean) && availableTargets.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </div>

      {applied ? (
        <div className="mt-5 p-4 bg-[#DCE8DF] border border-[#b3d0bb] rounded-md text-[#2f5238] flex items-center gap-2" data-testid={`applied-${idx}`}>
          <CheckCircle2 className="h-4 w-4" />
          <span className="text-sm">
            Imported <strong>{applied.created}</strong> new · <strong>{applied.updated}</strong> updated
          </span>
        </div>
      ) : (
        <div className="mt-6 flex justify-end">
          <Button onClick={onApply} disabled={busy} className="bg-[#4A6B5D] hover:bg-[#3E594D]" data-testid={`apply-sheet-${idx}`}>
            <CheckCircle2 className="h-4 w-4 mr-2" /> Import this sheet
          </Button>
        </div>
      )}
    </div>
  );
}
